import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Shield } from 'lucide-react';

const SectionText = ({ children }) => <p className="text-[#A1A1AA] leading-relaxed mb-2">{children}</p>;
const SectionList = ({ items }) => (
  <ul className="list-disc list-inside text-[#A1A1AA] space-y-2 ml-4">
    {items.map((item) => <li key={item}>{item}</li>)}
  </ul>
);

const SECTIONS = [
  {
    title: "1. Information We Collect",
    content: (
      <>
        <SectionText>We collect information you provide directly to us, including:</SectionText>
        <SectionList items={[
          'Account information (name, email, password)',
          'Conversation content and AI responses',
          'Payment information (processed securely through PayPal)',
          'Usage data and analytics',
        ]} />
      </>
    ),
  },
  {
    title: "2. How We Use Your Information",
    content: (
      <>
        <SectionText>Your information is used to:</SectionText>
        <SectionList items={[
          'Provide and improve our AI-powered support services',
          'Process payments and manage subscriptions',
          'Send important account and service updates',
          'Ensure content moderation and safety',
          'Analyze usage patterns to enhance user experience',
        ]} />
      </>
    ),
  },
  {
    title: "3. Data Security",
    content: (
      <>
        <SectionText>We implement industry-standard security measures:</SectionText>
        <SectionList items={[
          'Passwords are hashed using bcrypt encryption',
          'All data transmission uses HTTPS encryption',
          'Rate limiting and account lockout protection',
          'Regular security audits and updates',
        ]} />
      </>
    ),
  },
  {
    title: "4. Data Sharing",
    content: (
      <>
        <SectionText>We do not sell your personal information. We only share data with:</SectionText>
        <SectionList items={[
          'OpenAI (for AI response generation - subject to their privacy policy)',
          'PayPal (for payment processing - subject to their privacy policy)',
          'Email service providers (for transactional emails)',
          'Legal authorities when required by law',
        ]} />
      </>
    ),
  },
  {
    title: "5. Your Rights",
    content: (
      <>
        <SectionText>You have the right to:</SectionText>
        <SectionList items={[
          'Access your personal data',
          'Request data correction or deletion',
          'Export your conversation history',
          'Opt-out of non-essential communications',
          'Delete your account at any time',
        ]} />
      </>
    ),
  },
  {
    title: "6. Cookies and Tracking",
    content: <SectionText>We use essential cookies for authentication and session management. We do not use third-party tracking cookies for advertising purposes.</SectionText>,
  },
  {
    title: "7. Children's Privacy",
    content: (
      <>
        <SectionText>This service is rated <strong className="text-[#F9FAFB]">13+</strong> and is intended for users aged 13 and older. We believe young people deserve access to emotional support, and we've built safety measures including crisis detection and content moderation to protect all users.</SectionText>
        <SectionList items={[
          'Users must confirm they are 13 or older during registration',
          'We do not knowingly collect information from children under 13',
          'If we discover an account belongs to a user under 13, it will be removed',
          'Parents or guardians with concerns may contact us at any time',
        ]} />
      </>
    ),
  },
  {
    title: "8. Changes to This Policy",
    content: <SectionText>We may update this privacy policy from time to time. We will notify you of significant changes via email or in-app notification.</SectionText>,
  },
  {
    title: "9. Contact Us",
    content: (
      <SectionText>
        For privacy-related questions or concerns, contact us at:{' '}
        <a href="mailto:Mhvk2793@gmail.com" className="text-[#8B5CF6] hover:text-[#9D6FFF]">Mhvk2793@gmail.com</a>
      </SectionText>
    ),
  },
];

const PrivacyPage = () => (
  <div className="min-h-screen bg-[#0A0A0A] py-12 px-4" data-testid="privacy-page">
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-12">
        <Shield className="w-16 h-16 text-[#8B5CF6] mx-auto mb-4" />
        <h1 className="text-4xl sm:text-5xl font-light tracking-tighter mb-4" style={{ fontFamily: 'Outfit' }}>
          Privacy <span className="text-[#8B5CF6]">Policy</span>
        </h1>
        <p className="text-lg text-[#A1A1AA]">Last updated: January 2026</p>
      </div>
      <Card className="card-surface border-[#27272A]">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Outfit' }}>Your Privacy Matters</CardTitle>
        </CardHeader>
        <CardContent className="prose prose-invert max-w-none space-y-6">
          {SECTIONS.map((section) => (
            <section key={section.title}>
              <h3 className="text-xl font-semibold text-[#F9FAFB] mb-3" style={{ fontFamily: 'Outfit' }}>{section.title}</h3>
              {section.content}
            </section>
          ))}
        </CardContent>
      </Card>
    </div>
  </div>
);

export default PrivacyPage;
